import { Scissors, Trash2 } from "lucide-react";
import type { EditorItem, EditorPerson } from "../types/bills.types";
import { INPUT_CLASS } from "../types/bills.types";
import NumberField from "./NumberField";

interface ItemRowProps {
  item: EditorItem;
  people: EditorPerson[];
  readOnly: boolean;
  goDutch: boolean;
  onChange: (patch: Partial<EditorItem>) => void;
  onRemove: () => void;
  onToggle: (personId: string) => void;
  onClaimAll: () => void;
  onSplitUnits: () => void;
}

export default function ItemRow({
  item,
  people,
  readOnly,
  goDutch,
  onChange,
  onRemove,
  onToggle,
  onClaimAll,
  onSplitUnits,
}: ItemRowProps) {
  const unclaimed = !goDutch && item.claimedBy.length === 0;
  const zeroAmount = item.amount === 0;
  const allClaimed = item.claimedBy.length === people.length;

  return (
    <li
      className={`rounded-xl border bg-card p-3 ${
        unclaimed || zeroAmount ? "border-destructive/60" : "border-border"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <input
          aria-label="Item name"
          value={item.name}
          disabled={readOnly}
          placeholder="Item name"
          onChange={(e) => onChange({ name: e.target.value })}
          className={`${INPUT_CLASS} min-w-0 basis-full sm:basis-0 sm:flex-1`}
        />
        <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
        {item.quantity > 1 && (
          <span className="text-xs text-muted-foreground [font-variant-numeric:tabular-nums]">
            ×{item.quantity}
          </span>
        )}
        <NumberField
          ariaLabel="Item amount"
          value={item.amount}
          disabled={readOnly}
          onChange={(amount) => onChange({ amount })}
          className="w-24 shrink-0"
        />
        {!readOnly && (
          <button
            type="button"
            onClick={onRemove}
            aria-label="Delete item"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-destructive sm:h-8 sm:w-8"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!goDutch && people.map((p) => {
          const on = item.claimedBy.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              disabled={readOnly}
              onClick={() => onToggle(p.id)}
              aria-pressed={on}
              className={`min-h-11 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors active:scale-95 sm:min-h-0 sm:px-3 sm:py-1.5 ${
                on
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground"
              }`}
            >
              {p.name}
            </button>
          );
        })}
        {!goDutch && !readOnly && !allClaimed && people.length > 1 && (
          <button
            type="button"
            onClick={onClaimAll}
            className="min-h-11 rounded-full border border-dashed border-border px-3.5 py-2 text-xs text-muted-foreground active:scale-95 sm:min-h-0 sm:px-3 sm:py-1.5"
          >
            Everyone
          </button>
        )}
        {!readOnly && item.quantity > 1 && (
          <button
            type="button"
            onClick={onSplitUnits}
            className="ml-auto inline-flex min-h-11 items-center gap-1 rounded-full px-2.5 py-2 text-xs sm:min-h-0 sm:py-1.5 text-muted-foreground hover:text-foreground"
          >
            <Scissors className="w-3.5 h-3.5" />
            Split {item.quantity} units
          </button>
        )}
      </div>
      {unclaimed && !readOnly && (
        <p className="mt-1.5 text-xs text-destructive">
          Nobody assigned yet
        </p>
      )}
      {zeroAmount && !readOnly && (
        <p className="mt-1.5 text-xs text-destructive">
          Amount can't be 0
        </p>
      )}
    </li>
  );
}
